import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
  TablePagination,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddScopeItemDialog from './AddScopeItemDialog';
import ArchiveIcon from '@mui/icons-material/Archive';
import RestoreIcon from '@mui/icons-material/Restore';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import { useTranslation } from 'react-i18next';
import {
  scopeService,
  ScopeItem,
  ScopeStatus,
  ScopeType,
  type CreateScopeItemData,
} from '@services/scopeService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';

const SCOPE_TYPES: ScopeType[] = ['page', 'component', 'api', 'document', 'view', 'widget'];

interface ScopeInventoryTabProps {
  orgId: string;
  contextId: string;
  canManage?: boolean;
}

export default function ScopeInventoryTab({
  orgId,
  contextId,
  canManage = true,
}: ScopeInventoryTabProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<ScopeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ScopeStatus>('active');
  const [typeFilter, setTypeFilter] = useState<ScopeType | ''>('');
  const [criticalOnly, setCriticalOnly] = useState(false);

  // Pagination
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);

  // REV-500: the add form lives in a dialog; it owns its own field and error
  // state, so this only tracks whether it is open.
  const [addOpen, setAddOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await scopeService.list(orgId, contextId, {
        status: statusFilter,
        scope_type: typeFilter || undefined,
        is_critical_path: criticalOnly ? true : undefined,
        limit,
        offset,
      });
      setItems(res.data);
      setTotal(res.pagination.total);
    } catch (err) {
      setError(toDisplayError(err, 'Failed to load scope items'));
    } finally {
      setLoading(false);
    }
  }, [orgId, contextId, statusFilter, typeFilter, criticalOnly, limit, offset]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  /**
   * REV-500: the dialog's submit handler. Deliberately RETHROWS on failure —
   * the dialog keeps itself open and shows the error next to the input the user
   * still has. Swallowing it here would close the dialog and silently discard
   * everything they typed.
   */
  const handleAdd = async (data: CreateScopeItemData) => {
    await scopeService.create(orgId, contextId, { ...data, source: 'manual' });
    setOffset(0);
    await fetchItems();
  };

  const handleToggleCritical = async (item: ScopeItem) => {
    try {
      const updated = await scopeService.toggleCriticalPath(orgId, item.id, !item.is_critical_path);
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch (err) {
      setError(toDisplayError(err, 'Failed to update critical path'));
    }
  };

  const handleArchive = async (item: ScopeItem) => {
    try {
      await scopeService.archive(orgId, item.id);
      await fetchItems();
    } catch (err) {
      setError(toDisplayError(err, 'Failed to archive'));
    }
  };

  const handleRestore = async (item: ScopeItem) => {
    try {
      await scopeService.restore(orgId, item.id);
      await fetchItems();
    } catch (err) {
      setError(toDisplayError(err, 'Failed to restore'));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('scope.title', 'Scope Inventory')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'scope.subtitle',
          'Pages, components, and APIs in this context. Scope items are the denominator of coverage — critical-path items influence conformance rollup.'
        )}
      </Typography>

      {canManage && (
        <>
          {/* REV-500: adding is a dialog now. The inline form put three controls
              and a button permanently above the list, and had nowhere to put
              anything longer than one line — which is what component markup
              needs. */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{ mb: 3 }}
          >
            {t('scope.add.open', 'Add scope item')}
          </Button>
          <AddScopeItemDialog
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onSubmit={handleAdd}
          />
        </>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="scope-status-filter-label">
            {t('scope.filters.status', 'Status')}
          </InputLabel>
          <Select
            labelId="scope-status-filter-label"
            label={t('scope.filters.status', 'Status')}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as ScopeStatus);
              setOffset(0);
            }}
          >
            <MenuItem value="active">{t('scope.status.active', 'Active')}</MenuItem>
            <MenuItem value="archived">{t('scope.status.archived', 'Archived')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="scope-type-filter-label">{t('scope.filters.type', 'Type')}</InputLabel>
          <Select
            // REV-580: without this MUI renders a zero-width space for an
            // empty value, so the active default reads as 'no filter chosen'.
            displayEmpty
            labelId="scope-type-filter-label"
            label={t('scope.filters.type', 'Type')}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as ScopeType | '');
              setOffset(0);
            }}
          >
            <MenuItem value="">{t('scope.filters.anyType', 'Any')}</MenuItem>
            {SCOPE_TYPES.map((tp) => (
              <MenuItem key={tp} value={tp}>
                {tp}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            checked={criticalOnly}
            onChange={(e) => {
              setCriticalOnly(e.target.checked);
              setOffset(0);
            }}
            inputProps={{ 'aria-label': t('scope.filters.criticalOnly', 'Critical path only') }}
          />
          <Typography variant="body2">
            {t('scope.filters.criticalOnly', 'Critical path only')}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error && items.length === 0 ? null : items.length === 0 ? (
        // REV-821: a list that failed to load is not an empty place. Saying "No
        // scope items yet" beside the error would tell the user to add what
        // may already be there.
        <Alert severity="info">
          {statusFilter === 'archived'
            ? t('scope.empty.archived', 'No archived scope items.')
            : t('scope.empty.active', 'No scope items yet. Add a URL or component above to begin.')}
        </Alert>
      ) : (
        <>
          <ScrollableTableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('scope.table.identifier', 'Identifier')}</TableCell>
                  <TableCell>{t('scope.table.type', 'Type')}</TableCell>
                  <TableCell align="center">
                    {t('scope.table.criticalPath', 'Critical path')}
                  </TableCell>
                  <TableCell>{t('scope.table.status', 'Status')}</TableCell>
                  {canManage && (
                    <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.title || item.canonical_identifier}
                      </Typography>
                      {item.title && (
                        <Typography variant="caption" color="text.secondary">
                          {item.canonical_identifier}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={item.scope_type} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          item.is_critical_path
                            ? t('scope.actions.unflag', 'Unflag critical path')
                            : t('scope.actions.flag', 'Flag as critical path')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleCritical(item)}
                            disabled={!canManage || item.status === 'archived'}
                            aria-label={
                              item.is_critical_path
                                ? t('scope.actions.unflag', 'Unflag critical path')
                                : t('scope.actions.flag', 'Flag as critical path')
                            }
                            color={item.is_critical_path ? 'warning' : 'default'}
                          >
                            {item.is_critical_path ? <FlagIcon /> : <FlagOutlinedIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`scope.status.${item.status}`)}
                        color={item.status === 'active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        {item.status === 'active' ? (
                          <Tooltip title={t('scope.actions.archive', 'Archive')}>
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(item)}
                              aria-label={t('scope.actions.archive', 'Archive')}
                            >
                              <ArchiveIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('scope.actions.restore', 'Restore')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRestore(item)}
                              aria-label={t('scope.actions.restore', 'Restore')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
          <TablePagination
            component="div"
            count={total}
            page={Math.floor(offset / limit)}
            onPageChange={(_, newPage) => setOffset(newPage * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setOffset(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
}
